import React, { useMemo } from "react";
import { Table, Column, HeaderCell, Cell } from "rsuite-table";
import "rsuite-table/dist/css/rsuite-table.css";
import {
  AggregatedArticleData,
  ReminderView,
} from "../../../../types/reminderTypes";
import { useUnifiedFilters } from "../../../../hooks/useUnifiedFilters";
import { useAggregatedSearch } from "../../../../hooks/useViewSearch";
import styles from "./AggregatedView.module.scss";

interface AggregatedViewProps {
  data: AggregatedArticleData[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const AggregatedView: React.FC<AggregatedViewProps> = ({
  data,
  loading,
  error,
  onRefresh,
}) => {
  // Get search term from unified filters (URL)
  const { filters } = useUnifiedFilters(ReminderView.AGGREGATED);

  // Apply search at component level
  const { filteredData, totalResults, hasActiveSearch, searchTerm } =
    useAggregatedSearch(data, filters.search || "", loading);

  // Prepare filtered data for table display
  const tableData = useMemo(() => {
    return filteredData.map((item, index) => ({
      ...item,
      index: index + 1,
      shortageIndicator: item.totalQuantity > item.availableQuantity,
    }));
  }, [filteredData]);

  // Update summary stats to reflect filtered data
  const summaryStats = useMemo(() => {
    return {
      totalArticles: filteredData.length,
      totalQuantity: filteredData.reduce(
        (sum, item) => sum + item.totalQuantity,
        0
      ),
      totalReminders: filteredData.reduce(
        (sum, item) => sum + item.reminderCount,
        0
      ),
      shortageCount: filteredData.filter(
        (item) => item.totalQuantity > item.availableQuantity
      ).length,
    };
  }, [filteredData]);

  if (error) {
    return (
      <div className={styles.error}>
        <p>Došlo je do greške: {error}</p>
        <button onClick={onRefresh} className={styles.retryButton}>
          Pokušaj ponovo
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Učitavanje podataka...</span>
      </div>
    );
  }

  return (
    <div className={styles.aggregatedView}>
      {/* <div className={styles.header}>
        <h3>Zbirni prikaz artikala</h3>
        <p className={styles.description}>
          Lista artikala koji prispevaju u RFZO terapijama za izabrani period sa
          ukupnim količinama
        </p>
      </div> */}

      {/* Show search results info */}
      {hasActiveSearch && (
        <div className={styles.searchInfo}>
          Prikazano {totalResults} od {data.length} artikala
        </div>
      )}

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.cardValue}>{summaryStats.totalArticles}</div>
          <div className={styles.cardLabel}>
            {hasActiveSearch
              ? `Prikazano artikala (od ${data.length})`
              : "Ukupno artikala"}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardValue}>{summaryStats.totalQuantity}</div>
          <div className={styles.cardLabel}>Ukupna količina</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardValue}>{summaryStats.totalReminders}</div>
          <div className={styles.cardLabel}>Ukupno podsetnika</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.cardValue}>{summaryStats.shortageCount}</div>
          <div className={styles.cardLabel}>Artikli u manjku</div>
        </div>
      </div>

      <Table
        data={tableData}
        height={600}
        autoHeight
        bordered
        cellBordered
        hover
        rowClassName={(
          rowData: AggregatedArticleData & { shortageIndicator: boolean }
        ) => (rowData?.shortageIndicator ? styles.shortageRow : "")}
        renderEmpty={() => (
          <div className={styles.emptyState}>
            {searchTerm ? (
              <>Nema artikala koji odgovaraju pretrazi "{searchTerm}".</>
            ) : (
              "Nema rezultata za traženi datum."
            )}
          </div>
        )}
      >
        {/* Index */}
        <Column width={60} fixed="left" align="center">
          <HeaderCell>
            <span title="Redni broj">#</span>
          </HeaderCell>
          <Cell dataKey="index" verticalAlign="middle" />
        </Column>

        {/* Article Name */}
        <Column flexGrow={3} minWidth={250}>
          <HeaderCell>Naziv artikla</HeaderCell>
          <Cell verticalAlign="middle" dataKey="articleName" fullText={true}>
            {(
              rowData: AggregatedArticleData & { shortageIndicator: boolean }
            ) => (
              <div className={styles.articleNameCell}>
                {rowData.articleName}
                {rowData.shortageIndicator && (
                  <span
                    className={styles.warningIndicator}
                    title="Potrebna količina je veća od dostupne"
                  >
                    ⚠️
                  </span>
                )}
              </div>
            )}
          </Cell>
        </Column>

        {/* Total Quantity */}
        <Column width={120} align="center">
          <HeaderCell>Ukupna količina</HeaderCell>
          <Cell verticalAlign="middle" dataKey="totalQuantity">
            {(rowData: AggregatedArticleData) => (
              <span className={styles.quantityCell}>
                {rowData.totalQuantity}
              </span>
            )}
          </Cell>
        </Column>

        {/* Available Quantity */}
        <Column width={140} align="center">
          <HeaderCell>Raspoloživa količina</HeaderCell>
          <Cell verticalAlign="middle" dataKey="availableQuantity">
            {(
              rowData: AggregatedArticleData & { shortageIndicator: boolean }
            ) => (
              <span
                className={`${styles.quantityCell} ${
                  rowData.shortageIndicator ? styles.shortage : ""
                }`}
              >
                {rowData.availableQuantity}
              </span>
            )}
          </Cell>
        </Column>

        {/* Reminder Count */}
        <Column width={120} align="center">
          <HeaderCell>Broj podsetnika</HeaderCell>
          <Cell dataKey="reminderCount" verticalAlign="middle" />
        </Column>

        {/* Difference */}
        <Column width={120} align="center">
          <HeaderCell>Razlika</HeaderCell>
          <Cell verticalAlign="middle" dataKey="difference">
            {(rowData: AggregatedArticleData) => {
              const difference =
                rowData.availableQuantity - rowData.totalQuantity;
              return (
                <span
                  className={`${styles.differenceCell} ${
                    difference < 0 ? styles.negative : styles.positive
                  }`}
                >
                  {difference > 0 ? "+" : ""}
                  {difference}
                </span>
              );
            }}
          </Cell>
        </Column>
      </Table>
    </div>
  );
};

export default AggregatedView;
